import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";

export const useActiveTabLogic = ({ onTabChange, initialTab = null, tabs }) => {
  const location = useLocation();
  
  // State to track which dropdown is open
  const [openDropdown, setOpenDropdown] = useState(null);
  // State for mobile menu
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // State to track if a dropdown is expanded in mobile view
  const [mobileExpandedDropdown, setMobileExpandedDropdown] = useState(null);
  
  // Extract the current path and set the appropriate tab
  const getCurrentTabFromPath = () => {
    const path = location.pathname.split('/')[1] || '';
    // Return the path if it's a valid tab, otherwise null
    return tabs.some(tab => tab.id === path) ? path : null;
  };

  // State to track the active tab - initialize from initialTab prop or URL
  const [activeTab, setActiveTab] = useState(initialTab !== undefined ? initialTab : getCurrentTabFromPath());
  
  // Update active tab when URL or initialTab changes
  useEffect(() => {
    // If initialTab is explicitly provided (even if null), use it
    if (initialTab !== undefined) {
      setActiveTab(initialTab);
    } else {
      // Otherwise get it from the path
      const currentTab = getCurrentTabFromPath();
      setActiveTab(currentTab);
    }
  }, [location.pathname, initialTab]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isDropdownClick = event.target.closest('.dropdown-container');
      const isTabClick = event.target.closest('.tab-item');
      const isMobileMenuClick = event.target.closest('.mobile-menu-button');
      const isMobileMenuOption = event.target.closest('.mobile-menu-option');
      const isMobileDropdownOption = event.target.closest('.mobile-dropdown-option');
      
      if (!isDropdownClick && !isTabClick && !isMobileMenuClick && 
          !isMobileMenuOption && !isMobileDropdownOption) {
        setOpenDropdown(null);
        setMobileMenuOpen(false);
        setMobileExpandedDropdown(null);
        
        // Reset active tab to match the current URL path when dropdown closes
        setActiveTab(getCurrentTabFromPath());
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [location.pathname]); // Added location.pathname as dependency

  // Function to handle tab click
  const handleTabClick = (tabId, hasDropdown) => {
    // For tabs with dropdowns, don't navigate immediately
    if (hasDropdown) {
      setActiveTab(tabId);
      setOpenDropdown(openDropdown === tabId ? null : tabId);
      
      // If closing the dropdown, reset activeTab to match URL
      if (openDropdown === tabId) {
        setActiveTab(getCurrentTabFromPath());
      }
    } else {
      setActiveTab(tabId);
      setOpenDropdown(null);
      
      // Call the parent component's callback
      if (onTabChange) {
        onTabChange(tabId);
      }
    }
    
    // Close mobile menu when a tab is clicked
    setMobileMenuOpen(false);
  };

  // Add keyboard event listener for Escape key
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setOpenDropdown(null);
        setMobileMenuOpen(false);
        setMobileExpandedDropdown(null);
        
        // Reset active tab to match the current URL path
        setActiveTab(getCurrentTabFromPath());
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [location.pathname]); // Added location.pathname as dependency

  // Toggle mobile menu
  const toggleMobileMenu = (e) => {
    e.stopPropagation(); // Stop event propagation
    setMobileMenuOpen(!mobileMenuOpen);
    // Close any open dropdowns when toggling mobile menu
    setOpenDropdown(null);
    setMobileExpandedDropdown(null);
    
    // If closing the mobile menu, reset activeTab to match URL
    if (mobileMenuOpen) {
      setActiveTab(getCurrentTabFromPath());
    }
  };

  // Handle mobile menu item click
  const handleMobileMenuItemClick = (e, tabId, hasDropdown) => {
    e.stopPropagation(); // Stop event propagation
    
    if (hasDropdown) {
      // Toggle the expanded state for this dropdown
      setMobileExpandedDropdown(mobileExpandedDropdown === tabId ? null : tabId);
      setActiveTab(tabId);
      
      // If closing the dropdown, reset activeTab to match URL
      if (mobileExpandedDropdown === tabId) {
        setActiveTab(getCurrentTabFromPath());
      }
    } else {
      handleTabClick(tabId, hasDropdown);
    }
  };

  // Handle mobile dropdown option click
  const handleMobileDropdownOptionClick = (e, tabId, optionData) => {
    e.stopPropagation(); // Stop event propagation
    
    // If option is disabled, just return
    if (optionData.disabled) {
      return;
    }
    
    // Close mobile menu
    setMobileMenuOpen(false);
    setMobileExpandedDropdown(null);
    
    // Handle navigation
    handleDropdownNavigation(tabId, optionData);
  };

  // Generic function to handle navigation from any dropdown
  const handleDropdownNavigation = (tabId, optionData) => {
    // Close dropdown
    setOpenDropdown(null);
    
    // For learning dropdown, check if the option is disabled
    if (tabId === "learning") {
      if (optionData.disabled) {
        return; // Exit early if disabled
      }
      
      // FIX: Properly construct the path for navigation
      if (onTabChange) {
        // If optionData is an object with path property (from mobile navigation)
        if (optionData.path) {
          const path = `${tabId}/${optionData.path}`;
          console.log(`Navigating to: ${path}`);
          onTabChange(path);
        } 
        // If optionData is an index (from desktop navigation)
        else if (typeof optionData === 'number') {
          const options = getLearningDropdownOptions();
          if (options[optionData] && !options[optionData].disabled) {
            const path = `${tabId}/${options[optionData].path}`;
            console.log(`Navigating to: ${path}`);
            onTabChange(path);
          }
        }
      }
    } else if (tabId === "reports") {
      // Handle reports navigation when available
      console.log(`Selected reports option: ${optionData.index || optionData}`);
      // Add proper navigation logic here when reports are implemented
    } else if (tabId === "announcements") {
      // Handle announcements navigation when available
      const index = optionData.index !== undefined ? optionData.index : optionData;
      console.log(`Selected announcements option: ${index}`);
      if (onTabChange) {
        onTabChange(`${tabId}/${index === 0 ? 'all' : 'important'}`);
      }
    } else if (tabId === "chat") {
      // Handle chat navigation when available
      const index = optionData.index !== undefined ? optionData.index : optionData;
      console.log(`Selected chat option: ${index}`);
      if (onTabChange) {
        onTabChange(`${tabId}/${index === 0 ? 'direct' : 'group'}`);
      }
    }
  };

  // Get learning dropdown options
  const getLearningDropdownOptions = () => {
    return [
      { text: "Courses", path: "courses", disabled: false, index: 0 },
      { text: "Assignments", path: "assignments", disabled: true, index: 1 },
      { text: "Exams", path: "exams", disabled: true, index: 2 },
      { text: "Quiz", path: "quiz", disabled: true, index: 3 },
      { text: "Study Materials", path: "materials", disabled: false, index: 4 },
      { text: "Time Table", path: "timetable", disabled: true, index: 5 }
    ];
  };

  return {
    activeTab,
    openDropdown,
    mobileMenuOpen,
    mobileExpandedDropdown,
    handleTabClick,
    toggleMobileMenu,
    handleMobileMenuItemClick,
    handleMobileDropdownOptionClick,
    handleDropdownNavigation,
    getLearningDropdownOptions
  };
};